import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CardMeaning, DeckPublic } from '@tarot/contracts';
import { ApiError } from '@/api/client';
import { useBuy, useDecks, useMe, useSetPreset } from '@/api/hooks';
import { useUi } from '@/app/store';
import { Sheet } from '@/components/Sheet';
import { TarotCard } from '@/components/TarotCard';
import { Badge, Skeleton } from '@/components/ui';
import { platform } from '@/platform';
import { usePaywall } from '../paywall/Paywall';
import { CardFan } from '@/components/CardFan';
import { Icon } from '@/components/Icon';

export function DecksScreen() {
  const nav = useNavigate();
  const { data: me } = useMe();
  const { data } = useDecks();
  const setPreset = useSetPreset();
  const buy = useBuy();
  const toast = useUi((s) => s.showToast);
  const { paywall, openPaywall } = usePaywall();
  const [openDeck, setOpenDeck] = useState<DeckPublic | null>(null);
  const [openCard, setOpenCard] = useState<{ m: CardMeaning; d: DeckPublic } | null>(null);

  if (!me || !data) return <div className="screen"><Skeleton h={300} /></div>;

  const select = async (d: DeckPublic) => {
    try { await setPreset.mutateAsync({ deckId: d.id }); platform.haptic('success'); toast(`Колода «${d.title}» выбрана`); }
    catch (e) { toast((e as Error).message, true); }
  };
  const purchase = async (d: DeckPublic) => {
    try { await buy.mutateAsync({ kind: 'deck', id: d.id }); platform.haptic('success'); toast('Колода куплена'); }
    catch (e) { if (e instanceof ApiError && e.code === 'INSUFFICIENT_FUNDS') await openPaywall('funds', true); else toast((e as Error).message, true); }
  };
  const imgFor = (d: { cards: { key: string; imageUrl: string | null }[] }, key: string) => d.cards.find((c) => c.key === key)?.imageUrl ?? null;

  return (
    <div className="screen">
      <div className="topbar"><button className="back" onClick={() => nav(-1)}><Icon name="back" size={18} />Назад</button><div className="title">Колоды</div><span style={{ width: 60 }} /></div>
      <div className="stack">
        {data.decks.map((d) => {
          const active = me.activeDeckId === d.id;
          return (
            <div key={d.id} className={`card deck-tile${active ? ' selected' : ''}`}>
              {d.cards.some((c) => c.imageUrl) ? <CardFan images={[d.cards[0]?.imageUrl ?? null, d.cards[17]?.imageUrl ?? null, d.cards[19]?.imageUrl ?? null]} back={d.backStyle} size={44} /> : <div style={{ width: 54, flex: 'none' }}><TarotCard back={d.backStyle} /></div>}
              <div style={{ flex: 1 }}>
                <div className="row"><b>{d.title}</b><Badge rarity={d.rarity} /></div>
                <div className="dim">{d.description}</div>
                <div className="dim" style={{ fontStyle: 'italic' }}>{d.stylePrompt}</div>
              </div>
              <div className="stack" style={{ gap: 6 }}>
                {active ? <span className="pill gold"><Icon name="check" size={14} />Активна</span>
                  : d.owned ? <button className="btn ghost sm" onClick={() => select(d)}>Выбрать</button>
                  : d.priceCrystals !== null ? <button className="btn ghost sm" onClick={() => purchase(d)}><Icon name="crystal" size={14} className="c-crystal" />{d.priceCrystals}</button>
                  : <span className="dim">Из гачи</span>}
                <button className="btn ghost sm" onClick={() => setOpenDeck(d)}>Карты</button>
              </div>
            </div>
          );
        })}
      </div>

      <Sheet open={!!openDeck} onClose={() => setOpenDeck(null)}>
        {openDeck && (
          <div className="stack">
            <h2>{openDeck.title}</h2>
            <p className="muted">{data.meanings.length} карт · старшие арканы</p>
            <div className="cards-grid">
              {data.meanings.map((m) => (
                <div key={m.key} onClick={() => setOpenCard({ m, d: openDeck })}>
                  <TarotCard card={{ ...m, imageUrl: imgFor(data.decks.find((x) => x.id === openDeck.id)!, m.key) }} back={openDeck.backStyle} flipped className={openDeck.owned ? '' : 'locked'} />
                </div>
              ))}
            </div>
          </div>
        )}
      </Sheet>

      <Sheet open={!!openCard} onClose={() => setOpenCard(null)}>
        {openCard && (
          <div className="stack">
            <div style={{ maxWidth: 150, margin: '0 auto' }}><TarotCard card={openCard.m} back={openCard.d.backStyle} flipped /></div>
            <h2 className="center">{openCard.m.name}</h2>
            <p className="muted center" style={{ fontStyle: 'italic' }}>«{openCard.m.quote}»</p>
            <div className="card"><b>Прямое положение</b><p>{openCard.m.upright}</p></div>
            <div className="card"><b>Перевёрнутое положение</b><p>{openCard.m.reversed}</p></div>
            <div className="dim">Работа: {openCard.m.work} · Любовь: {openCard.m.love} · Здоровье: {openCard.m.health}</div>
          </div>
        )}
      </Sheet>
      {paywall}
    </div>
  );
}
